import React, { useState, useEffect, useRef, useCallback } from 'react'
import { getWebSocketUrl } from '../../config/websocket'
import './VoiceChat.scss'
import TextChat from '../TextChat/TextChat'

function useScrollHold(ref, dir) {
  const intervalRef = useRef(null)
  const start = () => {
    if (intervalRef.current) return
    intervalRef.current = setInterval(() => {
      ref.current?.scrollBy({ top: dir * 40, behavior: 'smooth' })
    }, 60)
  }
  const stop = () => { clearInterval(intervalRef.current); intervalRef.current = null }
  return { onMouseDown: start, onMouseUp: stop, onMouseLeave: stop, onTouchStart: start, onTouchEnd: stop }
}

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
    // TURN servers — required for mobile carrier NAT traversal
    {
      urls: [
        'turn:openrelay.metered.ca:80',
        'turn:openrelay.metered.ca:443',
        'turns:openrelay.metered.ca:443',
      ],
      username: 'openrelayproject',
      credential: 'openrelayproject',
    },
    {
      urls: [
        'turn:global.relay.metered.ca:80',
        'turn:global.relay.metered.ca:443',
        'turns:global.relay.metered.ca:443',
      ],
      username: 'openrelayproject',
      credential: 'openrelayproject',
    },
  ],
}

// Device detection
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
const supportsSinkId = typeof HTMLAudioElement !== 'undefined' && 'setSinkId' in HTMLAudioElement.prototype

function VoiceLevelBar({ level, className = '' }) {
  const pct = Math.min(100, Math.round((level / 255) * 100))
  const color = pct > 80 ? '#f44336' : pct > 50 ? '#ff9800' : '#4caf50'
  return (
    <div className={`voice-chat__level-bar ${className}`}>
      <div
        className="voice-chat__level-bar__fill"
        style={{ width: `${pct}%`, background: color }}
      />
    </div>
  )
}

const VoiceChat = ({ roomSlug, currentUser, isOwner = false, inviteCode, onKickUser, members = [] }) => {
  const wsRef = useRef(null)
  const peersRef = useRef({})
  const pendingCandidatesRef = useRef({})
  const localStreamRef = useRef(null)
  const analyserRefsMap = useRef({})
  const audioRefsMap = useRef({})
  const localAnimRef = useRef(null)
  const peerAnimsRef = useRef({})
  const selectedMicRef = useRef('')
  const peerVolumesRef = useRef({})
  const rawStreamRef = useRef(null)
  const audioProcessingCtxRef = useRef(null)
  const preAcquiredStreamRef = useRef(null) // mic pre-acquired during user gesture on mobile
  const vcListRef = useRef(null)
  const vcHoldUp = useScrollHold(vcListRef, -1)
  const vcHoldDown = useScrollHold(vcListRef, 1)

  const [isConnected, setIsConnected] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [participants, setParticipants] = useState([])
  const [micDevices, setMicDevices] = useState([])
  const [selectedMic, setSelectedMic] = useState('')
  const [error, setError] = useState('')
  const [localLevel, setLocalLevel] = useState(0)
  const [peerLevels, setPeerLevels] = useState({})
  const [peerVolumes, setPeerVolumes] = useState({})
  const [copied, setCopied] = useState(false)
  const isDeafenedRef = useRef(false)
  const mutedPeersRef = useRef(new Set())
  const [isDeafened, setIsDeafened] = useState(false)
  const [mutedPeers, setMutedPeers] = useState(new Set())
  const [participantSearch, setParticipantSearch] = useState('')
  const [outputDevices, setOutputDevices] = useState([])
  const [selectedOutput, setSelectedOutput] = useState('')
  const [iosUnlocked, setIosUnlocked] = useState(false)
  // Mobile: require user gesture before connecting (iOS needs getUserMedia in gesture context)
  const [wantConnect, setWantConnect] = useState(!isMobile)
  const [vcMobileTab, setVcMobileTab] = useState('voice') // kept for compat
  const [mobileVoiceExpanded, setMobileVoiceExpanded] = useState(false)

  // ─── Load microphone devices ────────────────────────────────────────────────
  const enumerateMics = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const mics = devices.filter(d => d.kind === 'audioinput')
      const outputs = devices.filter(d => d.kind === 'audiooutput')
      setMicDevices(mics)
      if (supportsSinkId) setOutputDevices(outputs)
      if (mics.length > 0 && !selectedMicRef.current) {
        setSelectedMic(mics[0].deviceId)
        selectedMicRef.current = mics[0].deviceId
      }
    } catch (_) {}
  }

  useEffect(() => {
    enumerateMics()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── WebSocket setup ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!roomSlug || !currentUser || !wantConnect) return

    const token = localStorage.getItem('token')
    const wsUrl = getWebSocketUrl(`ws/voice/${roomSlug}/`) + (token ? `?token=${token}` : '')
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      setIsConnected(true)
      setError('')
      startMedia(ws)
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        handleSignal(data)
      } catch (_) {}
    }

    ws.onclose = () => {
      setIsConnected(false)
    }

    ws.onerror = () => {
      setError('Ошибка подключения к голосовому чату')
    }

    return () => {
      sendLeave(ws)
      ws.close()
      cleanup()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomSlug, currentUser, wantConnect])

  // ─── Build Web Audio noise-reduction chain ──────────────────────────────────
  // Raw mic → highpass (cuts rumble) → lowpass (cuts hiss) → presence boost
  // → dynamics compressor (stable level) → gain → MediaStreamDestination
  // The resulting processed stream is fed into WebRTC instead of the raw stream.
  // NOTE: Skipped on iOS Safari — Web Audio + MediaStreamDestination is unreliable there.
  const processAudioStream = (rawStream) => {
    if (isMobile) return rawStream // Mobile (iOS + Android): AudioContext created outside
                                   // a user gesture starts SUSPENDED — the entire DSP chain
                                   // produces silence. Skip processing, send raw mic.
    try {
      if (audioProcessingCtxRef.current) {
        try { audioProcessingCtxRef.current.close() } catch (_) {}
      }
      // Do NOT pass sampleRate on iOS; it's already excluded above, but be safe
      const ctx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' })
      audioProcessingCtxRef.current = ctx

      const source = ctx.createMediaStreamSource(rawStream)

      // 1) High-pass: remove low-frequency rumble (fans, desk vibrations, HVAC)
      const highpass = ctx.createBiquadFilter()
      highpass.type = 'highpass'
      highpass.frequency.value = 100
      highpass.Q.value = 0.7

      // 2) Low-pass: remove high-frequency hiss / RF noise
      const lowpass = ctx.createBiquadFilter()
      lowpass.type = 'lowpass'
      lowpass.frequency.value = 8000
      lowpass.Q.value = 0.7

      // 3) Presence boost: lift 2.5–4kHz range for articulation & clarity
      const presence = ctx.createBiquadFilter()
      presence.type = 'peaking'
      presence.frequency.value = 3000
      presence.gain.value = 4
      presence.Q.value = 1.4

      // 4) Dynamics compressor: tames peaks, brings up quiet voice
      const compressor = ctx.createDynamicsCompressor()
      compressor.threshold.value = -26
      compressor.knee.value = 8
      compressor.ratio.value = 6
      compressor.attack.value = 0.003
      compressor.release.value = 0.15

      // 5) Post-compression gain makeup
      const gain = ctx.createGain()
      gain.gain.value = 1.3

      const destination = ctx.createMediaStreamDestination()

      source.connect(highpass)
      highpass.connect(lowpass)
      lowpass.connect(presence)
      presence.connect(compressor)
      compressor.connect(gain)
      gain.connect(destination)

      return destination.stream
    } catch (_) {
      return rawStream
    }
  }

  const startMedia = async (ws) => {
    try {
      let rawStream
      if (preAcquiredStreamRef.current) {
        // Mobile: mic was pre-acquired during handleConnectClick (user gesture) —
        // iOS Safari refuses getUserMedia called from ws.onopen (not a user gesture)
        rawStream = preAcquiredStreamRef.current
        preAcquiredStreamRef.current = null
      } else {
        // Desktop: calling getUserMedia here (ws.onopen) works fine
        const deviceId = selectedMicRef.current
        const audioConstraints = deviceId
          ? {
              deviceId: { exact: deviceId },
              echoCancellation: { ideal: true },
              noiseSuppression: { ideal: true },
              autoGainControl: { ideal: false },
              sampleRate: { ideal: 48000 },
              channelCount: { ideal: 1 },
              latency: { ideal: 0.01 },
            }
          : {
              echoCancellation: { ideal: true },
              noiseSuppression: { ideal: true },
              autoGainControl: { ideal: false },
              sampleRate: { ideal: 48000 },
              channelCount: { ideal: 1 },
              latency: { ideal: 0.01 },
            }
        rawStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints })
      }
      rawStreamRef.current = rawStream
      const processedStream = processAudioStream(rawStream)
      localStreamRef.current = processedStream
      // Mute microphone by default on join
      const track = processedStream.getAudioTracks()[0]
      if (track) { track.enabled = false; setIsMuted(true) }
      startLocalLevelMeter(processedStream)
      await enumerateMics()
      wsSend({ action: 'join', data: { user: currentUser.username } }, ws)
    } catch (err) {
      setError('Не удалось получить доступ к микрофону. Проверьте разрешения.')
    }
  }

  const restartMedia = async (deviceId) => {
    if (rawStreamRef.current) { rawStreamRef.current.getTracks().forEach(t => t.stop()); rawStreamRef.current = null }
    if (audioProcessingCtxRef.current) { try { audioProcessingCtxRef.current.close() } catch (_) {} audioProcessingCtxRef.current = null }
    if (localAnimRef.current) { cancelAnimationFrame(localAnimRef.current); localAnimRef.current = null }
    try {
      const audioConstraints = isMobile
        ? { deviceId: { exact: deviceId }, echoCancellation: true, noiseSuppression: true }
        : {
            deviceId: { exact: deviceId },
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: false },
            sampleRate: { ideal: 48000 },
            channelCount: { ideal: 1 },
            latency: { ideal: 0.01 },
          }
      const rawStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints })
      rawStreamRef.current = rawStream
      const processedStream = processAudioStream(rawStream)
      localStreamRef.current = processedStream
      startLocalLevelMeter(processedStream)
      const [newTrack] = processedStream.getAudioTracks()
      Object.values(peersRef.current).forEach(pc => {
        const sender = pc.getSenders().find(s => s.track?.kind === 'audio')
        if (sender && newTrack) sender.replaceTrack(newTrack)
      })
    } catch (err) {
      setError('Не удалось переключить микрофон')
    }
  }

  const handleMicChange = (e) => {
    const deviceId = e.target.value
    setSelectedMic(deviceId)
    selectedMicRef.current = deviceId
    if (isConnected) restartMedia(deviceId)
  }

  const startLocalLevelMeter = (stream) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      if (ctx.state === 'suspended') ctx.resume().catch(() => {})
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      const data = new Uint8Array(analyser.frequencyBinCount)
      const tick = () => {
        analyser.getByteFrequencyData(data)
        const avg = data.reduce((a, b) => a + b, 0) / data.length
        setLocalLevel(avg)
        localAnimRef.current = requestAnimationFrame(tick)
      }
      localAnimRef.current = requestAnimationFrame(tick)
    } catch (_) {}
  }

  const startPeerLevelMeter = (peerId, stream) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      if (ctx.state === 'suspended') ctx.resume().catch(() => {})
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyserRefsMap.current[peerId] = analyser
      const data = new Uint8Array(analyser.frequencyBinCount)
      const tick = () => {
        analyser.getByteFrequencyData(data)
        const avg = data.reduce((a, b) => a + b, 0) / data.length
        setPeerLevels(prev => ({ ...prev, [peerId]: avg }))
        peerAnimsRef.current[peerId] = requestAnimationFrame(tick)
      }
      peerAnimsRef.current[peerId] = requestAnimationFrame(tick)
    } catch (_) {}
  }

  const wsSend = (payload, wsInstance) => {
    const ws = wsInstance || wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload))
  }

  const sendLeave = (ws) => {
    if (currentUser) wsSend({ action: 'leave', data: { user: currentUser.username } }, ws)
  }

  const handleSignal = (data) => {
    const { action, from, data: payload } = data
    if (from === currentUser?.username) return

    switch (action) {
      case 'join':
        setParticipants(prev => [...prev.filter(p => p !== from), from])
        // Do NOT initiate here. The new joiner receives the 'users' list and sends
        // the offers. Creating a connection here causes a glare (double-offer) problem
        // where both sides are in 'have-local-offer' state and setRemoteDescription fails.
        break
      case 'offer':
        handleOffer(from, payload)
        break
      case 'answer':
        handleAnswer(from, payload)
        break
      case 'ice':
        handleIceCandidate(from, payload)
        break
      case 'leave':
        setParticipants(prev => prev.filter(p => p !== from))
        closePeer(from)
        break
      case 'users':
        if (Array.isArray(payload)) {
          const others = payload.filter(u => u !== currentUser?.username)
          setParticipants(prev => {
            const merged = [...new Set([...prev, ...others])]
            return merged
          })
          // Initiate connections to existing participants
          others.forEach(existingUser => {
            if (!peersRef.current[existingUser]) {
              createPeerConnection(existingUser, true)
            }
          })
        }
        break
      default:
        break
    }
  }

  const createPeerConnection = async (peerId, isInitiator) => {
    if (peersRef.current[peerId]) peersRef.current[peerId].close()
    const pc = new RTCPeerConnection(ICE_SERVERS)
    peersRef.current[peerId] = pc

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current))
    }

    pc.ontrack = (event) => {
      let remoteStream
      if (event.streams && event.streams[0]) {
        remoteStream = event.streams[0]
      } else {
        remoteStream = new MediaStream()
        remoteStream.addTrack(event.track)
      }
      playRemoteAudio(peerId, remoteStream)
      startPeerLevelMeter(peerId, remoteStream)
    }

    pc.onicecandidate = (event) => {
      if (event.candidate) wsSend({ action: 'ice', to: peerId, data: event.candidate })
    }

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed') {
        // ICE has definitively failed (e.g. both sides behind symmetric/carrier NAT
        // and TURN is unavailable). Request a new round of ICE gathering.
        try { pc.restartIce() } catch (_) {}
      }
    }

    if (isInitiator) {
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      wsSend({ action: 'offer', to: peerId, data: offer })
    }
  }

  const handleOffer = async (peerId, offer) => {
    // Always create a fresh non-initiator PC when receiving an offer.
    // Re-using an existing PC that already has a pending local offer set causes
    // InvalidStateError on setRemoteDescription (the glare / double-offer scenario).
    await createPeerConnection(peerId, false)
    const pc = peersRef.current[peerId]
    if (!pc) return
    await pc.setRemoteDescription(new RTCSessionDescription(offer))
    // Drain ICE candidates that arrived before the remote description was ready.
    const queued = pendingCandidatesRef.current[peerId] || []
    for (const c of queued) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)) } catch (_) {}
    }
    pendingCandidatesRef.current[peerId] = []
    const answer = await pc.createAnswer()
    await pc.setLocalDescription(answer)
    wsSend({ action: 'answer', to: peerId, data: answer })
  }

  const handleAnswer = async (peerId, answer) => {
    const pc = peersRef.current[peerId]
    if (!pc) return
    // Ignore stale/duplicate answers — only valid when we're waiting for one (have-local-offer)
    if (pc.signalingState !== 'have-local-offer') return
    await pc.setRemoteDescription(new RTCSessionDescription(answer))
    // Drain ICE candidates that arrived before the remote description was ready.
    const queued = pendingCandidatesRef.current[peerId] || []
    for (const c of queued) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)) } catch (_) {}
    }
    pendingCandidatesRef.current[peerId] = []
  }

  const handleIceCandidate = async (peerId, candidate) => {
    const pc = peersRef.current[peerId]
    if (pc && pc.remoteDescription) {
      try { await pc.addIceCandidate(new RTCIceCandidate(candidate)) } catch (_) {}
    } else {
      // Queue the candidate — will be drained after setRemoteDescription in handleOffer/handleAnswer.
      if (!pendingCandidatesRef.current[peerId]) pendingCandidatesRef.current[peerId] = []
      pendingCandidatesRef.current[peerId].push(candidate)
    }
  }

  const closePeer = (peerId) => {
    if (peersRef.current[peerId]) {
      peersRef.current[peerId].close()
      delete peersRef.current[peerId]
    }
    if (peerAnimsRef.current[peerId]) {
      cancelAnimationFrame(peerAnimsRef.current[peerId])
      delete peerAnimsRef.current[peerId]
    }
    delete pendingCandidatesRef.current[peerId]
    if (audioRefsMap.current[peerId]) {
      audioRefsMap.current[peerId].srcObject = null
      audioRefsMap.current[peerId].remove()
      delete audioRefsMap.current[peerId]
    }
    delete peerVolumesRef.current[peerId]
    setPeerLevels(prev => { const n = { ...prev }; delete n[peerId]; return n })
    setPeerVolumes(prev => { const n = { ...prev }; delete n[peerId]; return n })
  }

  const playRemoteAudio = (peerId, stream) => {
    let audio = audioRefsMap.current[peerId]
    if (!audio) {
      audio = document.createElement('audio')
      audio.autoplay = true
      audio.playsInline = true
      // Required for iOS: attach to DOM and be part of a visible element
      audio.setAttribute('playsinline', '')
      audio.setAttribute('webkit-playsinline', '')
      document.body.appendChild(audio)
      audioRefsMap.current[peerId] = audio
    }
    audio.srcObject = stream
    audio.volume = isDeafenedRef.current ? 0 : (peerVolumesRef.current[peerId] ?? 1.0)
    // Apply output device if selected (not iOS)
    if (selectedOutput && supportsSinkId && audio.setSinkId) {
      audio.setSinkId(selectedOutput).catch(() => {})
    }
    // Try to play; on mobile autoplay may be blocked by browser policy
    const playPromise = audio.play()
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay blocked — show unlock button so user can tap to enable audio
        if (isMobile) setIosUnlocked(false)
      })
    }
  }

  // Unlock audio on mobile after user gesture (required by iOS/Android autoplay policy)
  const unlockIOSAudio = () => {
    setIosUnlocked(true)
    Object.values(audioRefsMap.current).forEach(a => {
      a.play().catch(() => {})
    })
    if (audioProcessingCtxRef.current?.state === 'suspended') {
      audioProcessingCtxRef.current.resume().catch(() => {})
    }
  }

  // Tap-to-connect for mobile: pre-acquire mic during user gesture, then open WebSocket
  const handleConnectClick = async () => {
    setError('')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
      preAcquiredStreamRef.current = stream
      setIosUnlocked(false) // will show unlock button once connected
      setWantConnect(true)
    } catch (err) {
      setError('Нет доступа к микрофону. Разрешите доступ в настройках браузера.')
    }
  }

  const handleOutputChange = (e) => {
    const deviceId = e.target.value
    setSelectedOutput(deviceId)
    // Apply to all existing audio elements
    Object.values(audioRefsMap.current).forEach(audio => {
      if (audio.setSinkId) audio.setSinkId(deviceId).catch(() => {})
    })
  }

  const handleVolumeChange = (peerId, value) => {
    peerVolumesRef.current[peerId] = value
    setPeerVolumes(prev => ({ ...prev, [peerId]: value }))
    const audio = audioRefsMap.current[peerId]
    if (audio && !isDeafenedRef.current && !mutedPeersRef.current.has(peerId)) {
      audio.volume = value
    }
  }

  const cleanup = () => {
    if (localAnimRef.current) { cancelAnimationFrame(localAnimRef.current); localAnimRef.current = null }
    // Release pre-acquired stream if WS never connected (e.g. unmount before connection)
    if (preAcquiredStreamRef.current) { preAcquiredStreamRef.current.getTracks().forEach(t => t.stop()); preAcquiredStreamRef.current = null }
    // Stop the actual microphone (raw stream), not the virtual processed stream
    if (rawStreamRef.current) { rawStreamRef.current.getTracks().forEach(t => t.stop()); rawStreamRef.current = null }
    if (localStreamRef.current) { localStreamRef.current = null }
    if (audioProcessingCtxRef.current) { try { audioProcessingCtxRef.current.close() } catch (_) {} audioProcessingCtxRef.current = null }
    const peerIds = Object.keys(peersRef.current)
    peerIds.forEach(id => closePeer(id))
    peersRef.current = {}
    peerVolumesRef.current = {}
    setParticipants([])
    setLocalLevel(0)
    setPeerLevels({})
    setPeerVolumes({})
  }

  const toggleMute = async () => {
    if (!localStreamRef.current) return

    if (!isMuted) {
      // Fully stop raw tracks — releases hardware so recording can use the mic
      if (rawStreamRef.current) {
        rawStreamRef.current.getTracks().forEach(t => t.stop())
        rawStreamRef.current = null
      }
      // Disable the processed track so peers hear silence
      const track = localStreamRef.current.getAudioTracks()[0]
      if (track) track.enabled = false
      setIsMuted(true)
    } else {
      // Re-acquire mic and rebuild the audio pipeline
      try {
        const deviceId = selectedMicRef.current
        const audioConstraints = deviceId
          ? { deviceId: { exact: deviceId }, echoCancellation: { ideal: true }, noiseSuppression: { ideal: true }, autoGainControl: { ideal: false }, sampleRate: { ideal: 48000 }, channelCount: { ideal: 1 }, latency: { ideal: 0.01 } }
          : { echoCancellation: { ideal: true }, noiseSuppression: { ideal: true }, autoGainControl: { ideal: false }, sampleRate: { ideal: 48000 }, channelCount: { ideal: 1 }, latency: { ideal: 0.01 } }
        const rawStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints })
        rawStreamRef.current = rawStream
        // Rebuild Web Audio processing chain
        if (audioProcessingCtxRef.current) { try { audioProcessingCtxRef.current.close() } catch (_) {} audioProcessingCtxRef.current = null }
        if (localAnimRef.current) { cancelAnimationFrame(localAnimRef.current); localAnimRef.current = null }
        const processedStream = processAudioStream(rawStream)
        localStreamRef.current = processedStream
        startLocalLevelMeter(processedStream)
        // Replace the audio track in every active peer connection
        const [newTrack] = processedStream.getAudioTracks()
        Object.values(peersRef.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === 'audio')
          if (sender && newTrack) sender.replaceTrack(newTrack)
        })
        setIsMuted(false)
      } catch (err) {
        setError('Не удалось включить микрофон')
      }
    }
  }

  const leaveVoiceChat = () => {
    sendLeave()
    wsRef.current?.close()
    cleanup()
    setIsConnected(false)
  }

  const copyInviteCode = () => {
    if (inviteCode) {
      navigator.clipboard.writeText(inviteCode).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
    }
  }

  const toggleDeafen = () => {
    const next = !isDeafened
    isDeafenedRef.current = next
    setIsDeafened(next)
    Object.entries(audioRefsMap.current).forEach(([peerId, audio]) => {
      audio.volume = next ? 0 : (mutedPeersRef.current.has(peerId) ? 0 : (peerVolumesRef.current[peerId] ?? 1))
    })
  }

  const togglePeerMute = (peerId) => {
    const wasMuted = mutedPeersRef.current.has(peerId)
    if (wasMuted) {
      mutedPeersRef.current.delete(peerId)
    } else {
      mutedPeersRef.current.add(peerId)
    }
    const audio = audioRefsMap.current[peerId]
    if (audio) {
      audio.volume = !wasMuted ? 0 : (isDeafenedRef.current ? 0 : (peerVolumesRef.current[peerId] ?? 1))
    }
    setMutedPeers(new Set(mutedPeersRef.current))
  }

  const filteredParticipants = participantSearch.trim()
    ? participants.filter(p => p.toLowerCase().includes(participantSearch.toLowerCase()))
    : participants

  return (
    <div className="voice-chat__room">
      {/* Mobile-only: collapsible voice bar at top */}
      <div className="vc-mob-bar" onClick={() => setMobileVoiceExpanded(v => !v)}>
        <span className="vc-mob-bar__icon">🎙</span>
        <span className="vc-mob-bar__title">Голосовой</span>
        <span className={`vc-panel__dot ${isConnected ? 'on' : 'off'}`} />
        <svg className={`vc-mob-bar__chevron${mobileVoiceExpanded ? ' vc-mob-bar__chevron--up' : ''}`} viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M7 10l5 5 5-5z"/></svg>
      </div>

      {/* Left: compact voice panel */}
      <div className={`vc-panel${mobileVoiceExpanded ? ' vc-mob-open' : ' vc-mob-closed'}`}>
        <div className="vc-panel__header">
          <span className="vc-panel__title">🎙 Голосовой</span>
          <span className={`vc-panel__dot ${isConnected ? 'on' : 'off'}`} />
        </div>

        {error && <div className="vc-panel__error">{error}</div>}

        {/* Mobile: tap to connect (iOS needs getUserMedia called inside a user gesture) */}
        {isMobile && !wantConnect && (
          <button className="vc-panel__ios-unlock" onClick={handleConnectClick}>
            🎙 Нажмите для подключения
          </button>
        )}

        {micDevices.length >= 1 && (
          <select
            className="vc-panel__mic-select"
            value={selectedMic}
            onChange={handleMicChange}
            title="Выбор микрофона"
          >
            {micDevices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Mic ${i + 1}`}
              </option>
            ))}
          </select>
        )}

        {supportsSinkId && outputDevices.length > 0 && (
          <select
            className="vc-panel__mic-select"
            value={selectedOutput}
            onChange={handleOutputChange}
            title="Выбор динамика"
          >
            <option value="">🔊 Динамик по умолчанию</option>
            {outputDevices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Speaker ${i + 1}`}
              </option>
            ))}
          </select>
        )}

        {isMobile && isConnected && !iosUnlocked && (
          <button className="vc-panel__ios-unlock" onClick={unlockIOSAudio}>
            🔊 Активировать звук
          </button>
        )}

        <input
          className="vc-panel__search"
          type="text"
          placeholder="Поиск участников..."
          value={participantSearch}
          onChange={e => setParticipantSearch(e.target.value)}
        />

        <div className="vc-panel__list-wrapper">
        <div className="vc-panel__list" ref={vcListRef} data-lenis-prevent>
          {/* Self */}
          {(!participantSearch.trim() || currentUser?.username?.toLowerCase().includes(participantSearch.toLowerCase())) && (() => {
            const selfAvatar = currentUser?.avatar
              ? (currentUser.avatar.startsWith('http') ? currentUser.avatar : `https://security.ncore.kg${currentUser.avatar}`)
              : `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.username || 'U')}&background=7C3AED&color=fff&bold=true`
            const selfSpeaking = localLevel > 8 && !isMuted
            return (
              <div className="vc-panel__item vc-panel__item--self">
                <div className={`vc-panel__av-wrap${selfSpeaking ? ' spk' : ''}`}>
                  <img src={selfAvatar} alt="" className="vc-panel__av-img"
                    onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.username || 'U')}&background=7C3AED&color=fff&bold=true` }} />
                  {isMuted && <span className="vc-panel__muted-badge" title="Микрофон выключен">🔇</span>}
                </div>
                <div className="vc-panel__meta">
                  <div className="vc-panel__nm-row">
                    <span className="vc-panel__nm">{currentUser?.username}</span>
                    <span className="vc-panel__you">вы</span>
                  </div>
                  <div className="vc-panel__bar">
                    <div className="vc-panel__bar-fill" style={{ width: `${Math.min(100, (localLevel / 255) * 100)}%`, opacity: isMuted ? 0.2 : 1 }} />
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Peers */}
          {filteredParticipants.map(p => {
            const lvl = peerLevels[p] || 0
            const isMp = mutedPeers.has(p)
            const vol = peerVolumes[p] ?? 1
            const memberData = members.find(m => (m.user?.username || m.username) === p)
            const isSpeaking = lvl > 8 && !isMp && !isDeafened
            const peerAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(p)}&background=6D28D9&color=fff&bold=true`
            return (
              <div key={p} className={`vc-panel__item${isSpeaking ? ' vc-panel__item--speaking' : ''}`}>
                <div className={`vc-panel__av-wrap${isSpeaking ? ' spk' : ''}`}>
                  <img src={peerAvatar} alt={p} className="vc-panel__av-img" />
                  {isMp && <span className="vc-panel__muted-badge" title="Заглушен">🔇</span>}
                </div>
                <div className="vc-panel__meta">
                  <span className="vc-panel__nm">{p}</span>
                  <div className="vc-panel__bar">
                    <div className="vc-panel__bar-fill" style={{ width: `${Math.min(100, (lvl / 255) * 100)}%`, opacity: isMp || isDeafened ? 0.2 : 1 }} />
                  </div>
                  <input
                    type="range" min="0" max="1" step="0.05"
                    value={vol}
                    onChange={e => handleVolumeChange(p, parseFloat(e.target.value))}
                    className="vc-panel__vol"
                    disabled={isMp || isDeafened}
                    title={`${Math.round(vol * 100)}%`}
                  />
                </div>
                <div className="vc-panel__actions">
                  <button
                    className={`vc-panel__ibtn${isMp ? ' active' : ''}`}
                    onClick={() => togglePeerMute(p)}
                    title={isMp ? 'Включить звук' : 'Заглушить'}
                  >
                    {isMp ? (
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
                        <path d="M3.63 3.63a.996.996 0 000 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 101.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zm10.37 1.08v2.09l-2.1-2.1c.36-.2.77-.3 1.1-.3.74 0 1 .31 1 .31zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85 2.61.98 4.39 3.5 4.39 6.5z"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                      </svg>
                    )}
                  </button>
                  {isOwner && onKickUser && memberData && (
                    <button
                      className="vc-panel__ibtn vc-panel__ibtn--kick"
                      onClick={() => onKickUser(memberData.id || memberData.user?.id)}
                      title="Выгнать"
                    >✕</button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
        <div className="vc-panel__scroll-btns">
          <button className="vc-panel__scroll-btn" onClick={() => vcListRef.current?.scrollBy({ top: -140, behavior: 'smooth' })} {...vcHoldUp} title="Прокрутить вверх">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
          </button>
          <button className="vc-panel__scroll-btn" onClick={() => vcListRef.current?.scrollBy({ top: 140, behavior: 'smooth' })} {...vcHoldDown} title="Прокрутить вниз">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
        </div>
        </div>

        {/* Controls: mic + deafen only (no leave button) */}
        <div className="vc-panel__controls">
          <button
            className={`vc-panel__ctrl${isMuted ? ' active' : ''}`}
            onClick={toggleMute}
            disabled={!isConnected}
            title={isMuted ? 'Включить микрофон' : 'Выключить микрофон'}
          >
            {isMuted ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
              </svg>
            )}
          </button>
          <button
            className={`vc-panel__ctrl${isDeafened ? ' active' : ''}`}
            onClick={toggleDeafen}
            disabled={!isConnected}
            title={isDeafened ? 'Включить звук' : 'Заглушить всех'}
          >
            {isDeafened ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
            )}
          </button>
          {/* No leave button — voice disconnects when room is closed */}
        </div>
        {/* Mobile: "Open chat" button at bottom of voice panel */}
        <button className="vc-mob-chat-btn" onClick={(e) => { e.stopPropagation(); setMobileVoiceExpanded(false); }}>
          💬 Чат
        </button>

        <div className="vc-panel__privacy">Вы можете говорить свободно — разговор не фиксируется и не передаётся третьим лицам.</div>

        {isOwner && inviteCode && (
          <div className="vc-panel__invite">
            <code className="vc-panel__invite-code">{inviteCode}</code>
            <button className="vc-panel__invite-copy" onClick={copyInviteCode} title="Копировать">
              {copied ? '✓' : '📋'}
            </button>
          </div>
        )}
      </div>

      {/* Right: text chat — always visible */}
      <div className="voice-chat__text-area">
        <TextChat roomSlug={roomSlug} currentUser={currentUser} voiceMicActive={isConnected && !isMuted} />
      </div>
    </div>
  )
}

export default VoiceChat
